import {
  ErhlApplicationProcessSteps,
  ErhlProcessStep,
  ErhlProcessStepNames,
  StepStatus,
  type ActionType,
  type AppUser,
  type ErhlProcessStatus,
  type ErhlProcessStepEnum,
  type LoanApplication,
} from '@/types';
import { assign, createMachine } from 'xstate';
import { createLoanApp } from './builder';
import { ErhlEquityAccessMethod, ExitStrategyValue, LoanProductType, LoanType } from './const';
import { hasAssets } from './typeGuards';

export interface ErhlLoanAppMachineContext {
  loanApp: LoanApplication;
  from?: string;
  authorisedUser: Record<string, string>;
  status: ErhlProcessStatus;
  current: ErhlProcessStepEnum;
}

export const initMachine = (params?: {
  actionType: ActionType;
  userType: AppUser;
  brokerId?: string;
}): ErhlLoanAppMachineContext => ({
  loanApp: {
    ...createLoanApp(params),
    lendingProduct: 'erhl',
    loanType: LoanType.EQUITY_RELEASE_HOME_LOAN,
    erhlEquityAccessMethod: ErhlEquityAccessMethod.LUMP_SUM_AND_MONTHLY_DRAWDOWN,
  },
  authorisedUser: {},
  status: Object.fromEntries(
    ErhlProcessStepNames.map((step) => [
      step,
      step === ErhlProcessStep.LoanPurpose ? StepStatus.Ready : StepStatus.Incomplete,
    ]),
  ) as ErhlProcessStatus,
  current: ErhlProcessStep.LoanPurpose,
});

export type ErhlLoanAppMachineEvent =
  | { type: 'NEXT'; loanApp: LoanApplication }
  | { type: 'UPDATE'; loanApp: LoanApplication }
  | { type: 'BACK' }
  | { type: 'READY' }
  | { type: 'GOTO'; step: ErhlProcessStepEnum }
  | { type: 'RELOAD'; state: ErhlLoanAppMachineContext }
  | { type: 'RESTART' }
  | { type: 'RESTART_BROKER'; brokerId: string };

export const loanApplicationMachineErhl = createMachine<ErhlLoanAppMachineContext, ErhlLoanAppMachineEvent>(
  {
    id: 'loanApplicationErhl',
    predictableActionArguments: true,
    schema: {
      context: {} as ErhlLoanAppMachineContext,
    },
    context: initMachine(),
    initial: 'start',
    states: {
      start: {
        on: {
          NEXT: {
            target: 'gotoReady',
          },
        },
      },
      [ErhlProcessStep.LoanPurpose]: {
        on: {
          NEXT: {
            target: ErhlProcessStep.PropertyDetails,
            actions: 'saveData',
          },
        },
      },
      [ErhlProcessStep.PropertyDetails]: {
        on: {
          NEXT: {
            target: ErhlProcessStep.ApplicantDetails,
            actions: 'saveData',
          },
        },
      },
      [ErhlProcessStep.LoanDetails]: {
        on: {
          NEXT: {
            target: ErhlProcessStep.PropertyOwnership,
            actions: 'saveData',
          },
        },
      },
      [ErhlProcessStep.ExitStrategy]: {
        on: {
          NEXT: {
            target: ErhlProcessStep.SubmitApplication,
            actions: 'saveData',
          },
        },
      },
      [ErhlProcessStep.ApplicantDetails]: {
        on: {
          NEXT: {
            target: ErhlProcessStep.LoanDetails,
            actions: 'saveData',
          },
        },
      },
      [ErhlProcessStep.PropertyOwnership]: {
        on: {
          NEXT: {
            target: 'checkDebt',
            actions: 'saveData',
          },
        },
      },
      checkDebt: {
        always: [
          {
            target: ErhlProcessStep.Income,
            cond: 'showIncomeExpenses',
          },
          {
            target: ErhlProcessStep.Assets,
            cond: 'showAssets',
          },
          {
            target: ErhlProcessStep.SubmitApplication,
          },
        ],
      },
      [ErhlProcessStep.Income]: {
        on: {
          NEXT: {
            target: 'checkExpenses',
            actions: 'saveData',
          },
        },
      },
      checkExpenses: {
        always: [
          {
            target: ErhlProcessStep.Expenses,
            cond: 'showExpenses',
          },
          {
            target: ErhlProcessStep.Assets,
          },
        ],
      },
      [ErhlProcessStep.Expenses]: {
        on: {
          NEXT: {
            target: ErhlProcessStep.Assets,
            actions: 'saveData',
          },
        },
      },
      [ErhlProcessStep.Assets]: {
        on: {
          NEXT: [
            {
              cond: (ctx) =>
                ctx.loanApp.userType === 'Broker' ||
                !!ctx.loanApp.brokerId ||
                ctx.loanApp.actionType === 'updateExisting',
              target: ErhlProcessStep.ExitStrategy,
              actions: 'saveData',
            },
            {
              target: ErhlProcessStep.SubmitApplication,
              actions: 'saveData',
            },
          ],
        },
      },
      [ErhlProcessStep.SubmitApplication]: {
        on: {
          NEXT: {
            target: ErhlProcessStep.Finished,
            actions: 'saveData',
          },
        },
      },
      [ErhlProcessStep.Finished]: {
        on: {
          NEXT: {
            target: ErhlProcessStep.LoanPurpose,
            actions: 'resetLoanApplication',
          },
        },
      },
      prevStage: {
        always: [...ErhlProcessStepNames]
          .sort((a, b) => ErhlApplicationProcessSteps[b]?.order - ErhlApplicationProcessSteps[a]?.order)
          .map((step) => ({
            target: step,
            cond: (ctx) =>
              ErhlApplicationProcessSteps[ctx.current].order > ErhlApplicationProcessSteps[step].order &&
              ctx.status[step] === StepStatus.Completed,
          })),
      },
      gotoReady: {
        always: ErhlProcessStepNames.map((step) => ({
          target: step,
          cond: (ctx) => ctx.status[step] === StepStatus.Ready,
        })),
      },
    },
    on: {
      BACK: {
        target: 'prevStage',
        cond: (ctx) => ctx.current !== ErhlProcessStep.LoanPurpose,
      },
      READY: {
        target: 'gotoReady',
      },
      UPDATE: {
        actions: 'saveData',
      },
      GOTO: ErhlProcessStepNames.map((step) => ({
        target: step,
        cond: (ctx, evt) => evt.step === step,
      })),
      RELOAD: {
        target: 'gotoReady',
        actions: 'updateContext',
      },
      RESTART: {
        target: ErhlProcessStep.LoanPurpose,
        actions: 'resetLoanApplication',
      },
      RESTART_BROKER: {
        target: ErhlProcessStep.LoanPurpose,
        actions: 'resetBrokerApplication',
      },
    },
  },
  {
    actions: {
      saveData: assign((context, event) => {
        if (event.type !== 'NEXT' && event.type !== 'UPDATE') {
          return {};
        }

        const currentStep = ErhlApplicationProcessSteps[context.current];
        if (!currentStep) {
          return { loanApp: event.loanApp };
        }

        const status = Object.fromEntries(
          Object.entries(context.status)
            .filter(([step]) => ErhlApplicationProcessSteps[step as ErhlProcessStepEnum])
            .map(([step, status]) => [
              step,
              step === context.current
                ? StepStatus.Completed
                : ErhlApplicationProcessSteps[step as ErhlProcessStepEnum].order < currentStep.order
                ? status
                : StepStatus.Incomplete,
            ]),
        ) as ErhlProcessStatus;

        return { loanApp: event.loanApp, status };
      }),
      updateContext: assign((context, event) => (event.type === 'RELOAD' ? event.state : {})),
      resetLoanApplication: assign(() => initMachine({ userType: 'Primary', actionType: 'createNew' })),
      resetBrokerApplication: assign((context, event) =>
        initMachine({
          userType: 'Broker',
          brokerId: event.type === 'RESTART_BROKER' ? event.brokerId : '',
          actionType: 'createNew',
        }),
      ),
    },
    guards: {
      hasEndDebt: (ctx) => ctx.loanApp.endDebt > 0,
      showIncomeExpenses: (ctx) => {
        if (ctx.loanApp.lendingProduct === 'erhl') return true;

        const isCommercial = ctx.loanApp.productType === LoanProductType.COMMERCIAL;
        const hasEndDebt = ctx.loanApp.endDebt > 0;
        const isSingleSecurity = ctx.loanApp.loanType === LoanType.SINGLE_SECURITY;
        const isFromCrm = ctx.from === 'crm';

        if (isCommercial) return false;
        if (isFromCrm) return true;
        if (!hasEndDebt) return false;

        if (isSingleSecurity && ctx.loanApp.exitStrategy === ExitStrategyValue.SECURITY_REFINANCE) {
          return true;
        }

        if (!isSingleSecurity) return true;

        return hasEndDebt;
      },
      showAssets: (ctx) => {
        if (ctx.loanApp.lendingProduct === 'erhl') return true;

        const isSingleSecurity = ctx.loanApp.loanType === LoanType.SINGLE_SECURITY;
        const hasEndDebt = ctx.loanApp.endDebt > 0;
        if (isSingleSecurity) return true;
        if (hasEndDebt) return true;
        if (hasAssets(ctx.loanApp)) return true;
        return false;
      },
      hasNoBroker: (ctx) => ctx.loanApp.userType !== 'Broker',
      showExpenses: (ctx) => {
        if (ctx.loanApp.lendingProduct === 'erhl') return true;

        const isFromCrm = ctx.from === 'crm';
        const isRegulated = ctx.loanApp.assessmentType !== 'unregulated';
        if (isFromCrm) return true;
        if (!isRegulated) return false;
        return true;
      },
    },
  },
);
